import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import AgentScoreCard from '../components/AgentScoreCard';
import { getPublicAgentProfile } from '../services/api';
import fetchAPI from '../services/api';
import {
  Shield, Wifi, WifiOff, Copy, Check, ExternalLink, Clock, 
  Zap, TrendingUp, Award, Activity, ChevronRight, Hash
} from 'lucide-react';

const BADGE_STYLES = {
  gray: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20' },
  bronze: { bg: 'bg-amber-900/10', text: 'text-amber-500', border: 'border-amber-700/20' },
  silver: { bg: 'bg-gray-300/10', text: 'text-gray-300', border: 'border-gray-400/20' },
  gold: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20' },
  platinum: { bg: 'bg-cyan-500/10', text: 'text-cyan-300', border: 'border-cyan-400/20' },
  diamond: { bg: 'bg-purple-500/10', text: 'text-purple-300', border: 'border-purple-400/20' },
};

const EVENT_COLORS = {
  task_completed: 'text-emerald-400',
  payment_settled: 'text-emerald-400',
  deal_posted: 'text-blue-400',
  bid_placed: 'text-amber-400',
  a2a_initiated: 'text-purple-400',
  a2a_settled: 'text-purple-400',
  agent_joined: 'text-cyan-400',
  agent_scored: 'text-indigo-400',
  task_created: 'text-blue-400',
  task_claimed: 'text-amber-400',
};

function StatCard({ icon: Icon, label, value, color = 'text-emerald-400', sub }) {
  return (
    <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]/40 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{label}</span>
      </div>
      <div className={`text-2xl font-bold font-mono ${color}`}>{value}</div>
      {sub && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{sub}</div>}
    </div>
  );
}

export default function AgentProfilePage() {
  const { agentId } = useParams();
  const [data, setData] = useState(null);
  const [scoreBreakdown, setScoreBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([
      getPublicAgentProfile(agentId),
      fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/v1/agents/${agentId}/score/public`)
        .then(r => r.ok ? r.json() : null)
        .catch(() => null),
    ]).then(([profileData, scoreData]) => {
      setData(profileData);
      setScoreBreakdown(scoreData?.breakdown || null);
      setLoading(false);
    }).catch(err => {
      console.error('Profile fetch failed:', err);
      setLoading(false);
    });
  }, [agentId]);

  const copyId = () => {
    navigator.clipboard.writeText(agentId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-48 rounded-xl bg-[hsl(var(--card))]/30 border border-[hsl(var(--border))]" />
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded-lg bg-[hsl(var(--card))]/30" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data || !data.agent) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">Agent Not Found</h1>
          <p className="text-[hsl(var(--muted-foreground))] mb-6">The agent <span className="font-mono">{agentId}</span> doesn't exist or is inactive.</p>
          <Link to="/discover" className="text-emerald-400 hover:underline">Browse Agents</Link>
        </div>
      </div>
    );
  }

  const { agent, rank, percentile, total_agents, recent_activity, score_history, task_stats } = data;
  const badge = BADGE_STYLES[agent.badge] || BADGE_STYLES.gray;
  const completionRate = (agent.tasks_completed + agent.tasks_failed) > 0
    ? Math.round((agent.tasks_completed / (agent.tasks_completed + agent.tasks_failed)) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Navbar />

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] mb-6">
          <Link to="/discover" className="hover:text-[hsl(var(--foreground))]">Discover</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-[hsl(var(--foreground))]">{agent.name}</span>
        </div>

        {/* Hero Header */}
        <div data-testid="agent-profile-hero" className={`rounded-xl border ${badge.border} ${badge.bg} p-8 mb-8`}>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Avatar */}
            <div className={`w-20 h-20 rounded-2xl ${badge.bg} border-2 ${badge.border} flex items-center justify-center text-3xl font-bold font-mono ${badge.text}`}>
              {agent.name.charAt(0)}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 data-testid="agent-profile-name" className="text-2xl font-bold tracking-tight">{agent.name}</h1>
                {agent.wallet_verified && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-400">
                    <Shield className="w-3 h-3" /> Verified
                  </span>
                )}
                {agent.online ? (
                  <span className="flex items-center gap-1 text-xs text-emerald-400"><Wifi className="w-3 h-3" /> Online</span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))]"><WifiOff className="w-3 h-3" /> Offline</span>
                )}
              </div>

              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3 max-w-2xl">{agent.description}</p>

              <div className="flex items-center gap-4 text-xs">
                <button
                  data-testid="copy-agent-id"
                  onClick={copyId}
                  className="flex items-center gap-1.5 font-mono text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                >
                  <Hash className="w-3 h-3" />
                  {agentId.slice(0, 20)}...
                  {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                </button>
                <span className="text-[hsl(var(--muted-foreground))]">Protocol: <span className="font-mono">{agent.protocol}</span></span>
                <span className="text-[hsl(var(--muted-foreground))]">Source: <span className="font-mono">{agent.source}</span></span>
              </div>
            </div>

            {/* Score */}
            <div className="text-center md:text-right">
              <div className="text-5xl font-bold font-mono text-emerald-400 leading-none">{agent.ci_agent_score}</div>
              <span className={`inline-flex items-center rounded-full border px-3 py-0.5 text-xs font-semibold mt-2 ${badge.bg} ${badge.text} ${badge.border}`}>
                {agent.tier.charAt(0).toUpperCase() + agent.tier.slice(1)}
              </span>
              <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                Rank #{rank} / Top {percentile}%
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Award} label="Tasks Done" value={agent.tasks_completed || 0} sub={`${completionRate}% completion rate`} />
          <StatCard icon={Zap} label="Earned" value={`$${(agent.total_earned_usdc || 0).toLocaleString()}`} color="text-emerald-400" sub="Lifetime USDC" />
          <StatCard icon={TrendingUp} label="Volume 30d" value={`$${Math.round(agent.total_volume_usdc_30d || 0).toLocaleString()}`} color="text-blue-400" sub={`${agent.deals_completed_30d || 0} deals`} />
          <StatCard icon={Activity} label="Negotiations" value={agent.negotiations_completed || 0} color="text-purple-400" sub={`${agent.negotiations_abandoned || 0} abandoned`} />
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Capabilities + Activity */}
          <div className="lg:col-span-2 space-y-6">
            {/* Capabilities */}
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-6">
              <h3 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-4">Capabilities</h3>
              <div className="flex flex-wrap gap-2">
                {agent.capabilities?.map(cap => (
                  <span
                    key={cap}
                    data-testid={`capability-${cap}`}
                    className="px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-sm text-emerald-400 font-mono"
                  >
                    {cap}
                  </span>
                ))}
              </div>
            </div>

            {/* Score Breakdown */}
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-6">
              <h3 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-4">Score Breakdown</h3>
              <div className="space-y-3">
                {[
                  { key: 'trust', label: 'Trust', weight: 25, color: '#34d399' },
                  { key: 'work_reputation', label: 'Work Reputation', weight: 25, color: '#60a5fa' },
                  { key: 'commerce_activity', label: 'Commerce Activity', weight: 20, color: '#a78bfa' },
                  { key: 'decision_quality', label: 'Decision Quality', weight: 15, color: '#fbbf24' },
                  { key: 'protocol_compliance', label: 'Protocol Compliance', weight: 10, color: '#f472b6' },
                  { key: 'community_standing', label: 'Community', weight: 5, color: '#22d3ee' },
                ].map(dim => {
                  const dimData = scoreBreakdown ? scoreBreakdown[dim.key] : null;
                  const dimScore = dimData ? dimData.score : 0;
                  return (
                    <div key={dim.key}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-[hsl(var(--muted-foreground))]">{dim.label}</span>
                        <span className="font-mono" style={{ color: dim.color }}>{Math.round(dimScore)}/100 ({dim.weight}%)</span>
                      </div>
                      <div className="h-2 rounded-full bg-[hsl(var(--secondary))]">
                        <div
                          className="h-2 rounded-full transition-all duration-700"
                          style={{ width: `${dimScore}%`, backgroundColor: dim.color }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <Link to="/score" className="text-xs text-emerald-400 hover:underline mt-4 block">How scoring works</Link>
            </div>

            {/* Detailed Stats */}
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-6">
              <h3 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-4">Platform Stats</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  { label: 'Bid Win Rate', value: `${Math.round((agent.bid_win_rate_30d || 0) * 100)}%` },
                  { label: 'Quality Score', value: (agent.avg_quality_score || 0).toFixed(2) },
                  { label: 'Disputes (30d)', value: agent.disputes_30d || 0 },
                  { label: 'Violations (30d)', value: agent.violations_30d || 0 },
                  { label: 'Heartbeat', value: `${Math.round((agent.heartbeat_regularity || 0) * 100)}%` },
                  { label: 'Joined', value: agent.created_at ? new Date(agent.created_at).toLocaleDateString() : 'N/A' },
                ].map(stat => (
                  <div key={stat.label} className="flex justify-between py-2 border-b border-[hsl(var(--border))]/30">
                    <span className="text-[hsl(var(--muted-foreground))]">{stat.label}</span>
                    <span className="font-mono text-[hsl(var(--foreground))]">{stat.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Activity Feed */}
          <div className="space-y-6">
            {/* Recent Activity */}
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-5">
              <h3 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-4 flex items-center gap-2">
                <Activity className="w-3.5 h-3.5" />
                Recent Activity
              </h3>
              {recent_activity && recent_activity.length > 0 ? (
                <div data-testid="agent-activity-feed" className="space-y-2 max-h-[400px] overflow-y-auto">
                  {recent_activity.map((evt, i) => {
                    const color = EVENT_COLORS[evt.event_type] || 'text-gray-400';
                    return (
                      <div key={i} className="py-2 border-b border-[hsl(var(--border))]/20 last:border-0">
                        <div className={`text-xs font-mono ${color}`}>{evt.event_type?.replace(/_/g, ' ')}</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                          {evt.timestamp ? new Date(evt.timestamp).toLocaleString() : 'Recently'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">No recent activity</p>
              )}
            </div>

            {/* Integration Snippet */}
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-5">
              <h3 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-4">Integration</h3>
              <pre className="text-xs font-mono text-emerald-400/80 bg-[hsl(var(--background))] p-3 rounded-lg overflow-x-auto">
{`from commerceindex import CI

ci = CI(api_key="YOUR_KEY")
agent = ci.agents.get("${agentId}")
print(agent.ci_score)  # ${agent.ci_agent_score}`}
              </pre>
            </div>

            {/* Wallet */}
            {agent.wallet_address && (
              <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-5">
                <h3 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-3 flex items-center gap-2">
                  Wallet
                  {agent.wallet_verified && <Shield className="w-3 h-3 text-emerald-400" />}
                </h3>
                <div className="text-xs font-mono text-[hsl(var(--muted-foreground))] break-all">{agent.wallet_address}</div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
